import {
  useGetAllCentersQuery,
  useAddCenterMutation,
  useGetCenterByIdQuery,
  useUpdateCenterMutation,
  useDeleteCenterMutation,
} from "../api/centerApi";
import { CenterResponseType } from "../types/centerResponse";
import { CenterPayloadType } from "../types/centerRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { PaginationProps } from "../../../../core/data/interface";

export const useCenter = (params?: {
  size?: number;
  search?: string;
  page?: number;
  zone?: string;
  district?: string;
  type?: string;
  sort_by?: string;
  sort_order?: string;
} & { skip?: boolean }) => {
  const { data, isLoading, isError, error } = useGetAllCentersQuery(
    params || {},
    { skip: params?.skip }
  );

  const [
    addCenterMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddCenterMutation();

  const [
    updateCenterMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateCenterMutation();

  const [
    deleteCenterMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteCenterMutation();

  const errorMessage = isError ? getApiErrorMessage(error) : null;
  const addErrorMessage = isAddError ? getApiErrorMessage(addError) : null;
  const updateErrorMessage = isUpdateError
    ? getApiErrorMessage(updateError)
    : null;
  const deleteErrorMessage = isDeleteError
    ? getApiErrorMessage(deleteError)
    : null;
  const centers: CenterResponseType[] = data?.data?.items || [];

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const addNewCenter = async (payload: CenterPayloadType) => {
    try {
      const response = await addCenterMutation(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: addErrorMessage || "Failed to add center",
      };
    }
  };

  const useGetCenterById = (id: string) => {
    return useGetCenterByIdQuery(
      { id },
      {
        skip: !id,
      }
    );
  };

  const updateCenter = async (id: string, payload: CenterPayloadType) => {
    try {
      const response = await updateCenterMutation({ id, payload }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: updateErrorMessage || "Failed to update center",
      };
    }
  };

  const deleteCenter = async (id: string) => {
    try {
      const response = await deleteCenterMutation({ id }).unwrap();
      return { success: true, data: response.data };
    } catch (error: any) {
      if (error.status === 409) {
        return { 
          success: false, 
          error: "This center has associated records that prevent deletion. Would you like to deactivate the center instead?",
          status: 409 
        };
      }
      return {
        success: false,
        error: getApiErrorMessage(error) || "Could not delete the center",
      };
    }
  };

  const getCenterState = () => ({
    centers,
    isLoading,
    error: errorMessage,
  });

  return {
    getCenterState,
    useGetCenterById,
    addNewCenter,
    updateCenter,
    deleteCenter,
    isAdding,
    isUpdating,
    isDeleting,
    addError: addErrorMessage,
    updateError: updateErrorMessage,
    deleteError: deleteErrorMessage,
    paginationData,
  };
};
